import { useEffect } from "react";

const SITE_URL = "https://fastrupees.com";
const DEFAULT_IMAGE = `${SITE_URL}/fastrupeeslogo-og.png`;

interface SeoProps {
  title: string;
  description: string;
  /** Path only, e.g. "/about" — used for canonical + og:url */
  path: string;
  image?: string;
  noindex?: boolean;
  /** One JSON-LD object, or an array of them, injected as <script type="application/ld+json"> */
  schema?: object | object[];
}

// Reuses the static tag already present in index.html (if any) instead of
// creating a duplicate, then marks it so future updates target the same node.
function setMetaByName(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("data-seo", "true");
  tag.setAttribute("content", content);
}

function setMetaByProperty(property: string, content: string) {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("data-seo", "true");
  tag.setAttribute("content", content);
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("data-seo", "true");
  link.setAttribute("href", href);
}

const Seo = ({ title, description, path, image, noindex, schema }: SeoProps) => {
  useEffect(() => {
    const fullTitle = `${title} | FastRupees`;
    const url = `${SITE_URL}${path}`;
    const rawImage = image || DEFAULT_IMAGE;
    const ogImage = rawImage.startsWith("http") ? rawImage : `${SITE_URL}${rawImage}`;

    document.title = fullTitle;

    setMetaByName("description", description);
    setMetaByName("robots", noindex ? "noindex, nofollow" : "index, follow");

    setMetaByProperty("og:type", "website");
    setMetaByProperty("og:site_name", "FastRupees");
    setMetaByProperty("og:title", fullTitle);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", url);
    setMetaByProperty("og:image", ogImage);

    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", fullTitle);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", ogImage);

    setCanonical(url);

    const scripts: HTMLScriptElement[] = [];
    if (schema) {
      const items = Array.isArray(schema) ? schema : [schema];
      items.forEach((item) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-schema", "true");
        script.text = JSON.stringify(item);
        document.head.appendChild(script);
        scripts.push(script);
      });
    }

    return () => {
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, path, image, noindex, schema]);

  return null;
};

export default Seo;
